import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { FuseTranslationLoaderService } from '../../../../../@fuse/services/translation-loader.service';
import { sentry } from '../../../../../sentry';
import { ActionTypeEnum, ListItem } from '../../../../core/components/fc-list-item/fc-list-item.types';
import { CustomMessagesService } from '../../../../core/errors/custom-messages.service';
import { Equipment } from '../../../../core/interfaces';
import { fetchAllInBatches } from '../../../../core/utils/fetch-all-in-batches';
import { EquipmentsServiceApi } from '../dialog-location/tabs/links/services/equipment/equipment.api';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

export interface DialogLinkEquipmentData {
  locationId: string;
  locationSectorId: string;
  locationEnvironmentId: string | null;

}

export interface DialogLinkEquipmentResult {
  reload?: boolean;
}

@Component({
  templateUrl: './dialog-link-equipment.component.html',
  styleUrls: ['./dialog-link-equipment.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogLinkEquipmentComponent implements OnInit {

  public readonly searchTerm = new FormControl();
  public items = [];

  public isSaving = false;
  public isLoading = false;

  public maxItems = 0;
  private readonly destroyed$ = new Subject<void>();
  private readonly equipmentIdsToLink = new Set<string>();
  private readonly equipmentIdsToUnlink = new Set<string>();

  public selectedCount = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: DialogLinkEquipmentData,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly equipmentsServiceApi: EquipmentsServiceApi,
    private readonly matDialogRef: MatDialogRef<DialogLinkEquipmentResult>,
    private readonly messageService: CustomMessagesService,
    private readonly translateService: TranslateService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public async ngOnInit(): Promise<void> {

    this.searchTerm.valueChanges.pipe(
      takeUntil(this.destroyed$),
      debounceTime(1000),
      switchMap(() => this.searchEquipments())
    ).subscribe(() => {
      this.searchEquipments();
    });

    await this.searchEquipments();
  }

  public async searchEquipments(): Promise<void> {
    try {
      this.isLoading = true;
      this.items = await fetchAllInBatches(this.equipmentsServiceApi, {
        where: {
          locationId_in: [this.data.locationId],
          name_or_number_contains: this.searchTerm.value,
        }
      }).then((equipments) => equipments.map((equipment) => this.toListItem(equipment)));

      this.selectedCount = this.items.filter((item) => item.selected).length;
      this.maxItems = this.items.length;
    } finally {
      this.isLoading = false;
    }
  }

  public async save(): Promise<void> {
    try {
      this.isSaving = false;
      const itemsToUnlink = [...this.equipmentIdsToUnlink.values()].map((id) => {
        return {
          equipmentId: id,
          locationId: this.data.locationId,
          locationSectorId: this.data.locationEnvironmentId ? this.data.locationSectorId : null,
          locationEnvironmentId: null
        };
      });

      const itemsToLink = [...this.equipmentIdsToLink.values()].map((id) => {
        return {
          equipmentId: id,
          locationId: this.data.locationId,
          locationSectorId: this.data.locationSectorId,
          locationEnvironmentId: this.data.locationEnvironmentId
        };
      });

      if (itemsToLink.length || itemsToUnlink.length) {
        await this.equipmentsServiceApi.linkEquipment({
          items: [...itemsToLink, ...itemsToUnlink],
        });
      }


      this.messageService.success(this.translateService.instant('LINK_EQUIPMENTS_DIALOG.LINK_SUCCESS_MESSAGE'));

      this.matDialogRef.close({ reload: true });
    } catch (error) {
      sentry.captureException(error);
      this.messageService.error(error);
    } finally {
      this.isSaving = false;
    }
  }

  private toListItem(equipment: Equipment): ListItem<Equipment> {
    return {
      id: equipment.id,
      text: equipment.name,
      description: equipment.number,
      action: {
        type: ActionTypeEnum.checkbox,
        checked: false,
        disabled: false,
      },
      archived: equipment.archived,
      archivedText: this.translateService.instant('LINK_EQUIPMENTS_DIALOG.ARCHIVED'),
      item: equipment,
      image: equipment.imageUrl,
      selected: this.isSelected(equipment)
    };
  }

  private isSelected(equipment: Equipment): boolean {
    return this.data.locationEnvironmentId
      ? this.data.locationEnvironmentId === (equipment.locationEnvironment && equipment.locationEnvironment.id)
      : this.data.locationSectorId === (equipment.locationSector && equipment.locationSector.id);
  }

  public onSelectedItemChange(item: ListItem<Equipment>): void {
    if (item.selected) {
      this.equipmentIdsToLink.add(item.id);
      this.equipmentIdsToUnlink.delete(item.id);
    } else {
      this.equipmentIdsToLink.delete(item.id);
      this.equipmentIdsToUnlink.add(item.id);
    }

    this.selectedCount = this.items.filter((currentItem) => currentItem.selected).length;
  }
}
